import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import ReactInputMask from 'react-input-mask';
import PlaceIcon from '@material-ui/icons/Place';
import WarningIcon from '@material-ui/icons/ReportProblem';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { InspectionItemType } from '../../../../types';
import { Loader } from '../../../../../../atoms';
import DatePickerControl from '../../../Common/Controls/DatePickerControl';
import { setInspectionApiResults } from '../../../../../../redux/slices/inspectionsSlice';
import { RootState } from '../../../../../../redux/root';
import ContactDropdown from './ContactDropdown';
import {
  setContact,
  setContactPhone,
  setErrors,
  setNotes,
} from '../../../../../../redux/slices/InspectionsReportSlice';

type Props = {
  inspection: InspectionItemType;
  setInspectionDate: Function;
};


const InspectionsControls: FC<Props> = ({
  inspection,
  setInspectionDate,
}) => {
  const [date, setDate] = useState<string>(inspection.inspectionDate);
  const dispatch = useDispatch();
  const { isApiError, apiMessage } = useSelector(
    (s: RootState) => s.inspections,
  );
  const { contactPhone, errors, allContacts, contact, notes } =
    useSelector((s: RootState) => s.inspectionReports);

  useEffect(() => {
    return () => {
      dispatch(
        setInspectionApiResults({
          apiMessage: undefined,
          isApiError: false,
        }),
      );
    };
  }, []);

  useEffect(() => {
    if (allContacts.length > 0 && inspection.contactId) {
      const selContact = allContacts.find(
        c => c.id === Number(inspection.contactId),
      );
      if (selContact) dispatch(setContact(selContact));
    }
  }, [allContacts]);

  useEffect(() => {
    dispatch(setContactPhone(contact?.phoneNumber || ''));
  }, [contact]);

  const setDateValue = (e: any) => {
    if (errors.inspectionDate) {
      const _errors = { ...errors };
      delete _errors.inspectionDate;
      dispatch(setErrors(_errors));
    }
    setDate(e);
    setInspectionDate(e);
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setContactPhone(e.target.value));
    dispatch(setErrors({ ...errors, contactPhone: null }));
  };

  const projectAddress = inspection.projectAddress || '—';
  const projectName = inspection.projectName || '—';
  const permitName = inspection.permitName || '—';
  const jurisdiction = inspection.projectJurisdiction || '—';
  const manager = [
    inspection.managerFirstName,
    inspection.managerLastName,
  ]
    .filter(Boolean)
    .join(' ') || '—';
  const inspectionNumber = inspection.id ? `${inspection.id}` : '—';

  const isHeaderLoading =
    !inspection.id ||
    (!inspection.projectName && !inspection.projectAddress);
  const isContactsLoading = allContacts.length === 0;

  return (
    <Root>
      {isHeaderLoading && (
        <LoadingCard role="status" aria-live="polite">
          <Loader size={28} thickness={4} />
          <LoadingText>Loading inspection details…</LoadingText>
        </LoadingCard>
      )}
      {!isHeaderLoading && (
        <HeaderCard>
          <HeaderCol>
            <HeaderLabel>Project / Permit</HeaderLabel>
            <HeaderTitle title={`${projectName} / ${permitName}`}>
              {projectName}
              <Slash> / </Slash>
              {permitName}
            </HeaderTitle>
            <HeaderAddress title={projectAddress}>
              <PlaceIcon style={{ fontSize: 16 }} />
              <span>{projectAddress}</span>
            </HeaderAddress>
          </HeaderCol>
          <HeaderDivider />
          <HeaderMeta>
            <MetaRow>
              <MetaLabel>Jurisdiction</MetaLabel>
              <MetaValue>{jurisdiction}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Project Manager</MetaLabel>
              <MetaValue>{manager}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Inspection #</MetaLabel>
              <MetaValue>{inspectionNumber}</MetaValue>
            </MetaRow>
          </HeaderMeta>
        </HeaderCard>
      )}

      {!isHeaderLoading && (
        <>
          <SectionEyebrow>Inspection Details</SectionEyebrow>
          <FormGrid>
            <Field>
              <FieldLabel>Inspection Type</FieldLabel>
              <FieldShell readOnly>
                <ReadOnlyValue>
                  {inspection.activityTypeName || '—'}
                </ReadOnlyValue>
              </FieldShell>
            </Field>

            <Field>
              <FieldLabel>Inspection Status</FieldLabel>
              <FieldShell readOnly>
                <ReadOnlyValue>
                  {inspection.statusName || '—'}
                </ReadOnlyValue>
              </FieldShell>
            </Field>
          </FormGrid>
        </>
      )}

      {!isHeaderLoading && (
      <TripleGrid>
        <Field>
          <FieldLabel error={!!errors.inspectionDate}>
            Inspection Date <Required>*</Required>
          </FieldLabel>
          <FieldShell error={!!errors.inspectionDate}>
            <DatePickerControl
              value={date ? new Date(date) : null}
              onChange={setDateValue}
            />
          </FieldShell>
          {errors.inspectionDate && (
            <ErrorText>{errors.inspectionDate}</ErrorText>
          )}
        </Field>

        <Field>
          <FieldLabel error={!!errors.contactPhone}>
            Phone Number <Required>*</Required>
          </FieldLabel>
          <FieldShell error={!!errors.contactPhone}>
            <ReactInputMask
              mask="999-999-9999"
              maskChar=" "
              value={contactPhone}
              onChange={onPhoneChange}
            >
              {() => (
                <PhoneInput
                  name="phone"
                  value={contactPhone}
                  onChange={onPhoneChange}
                  placeholder="###-###-####"
                />
              )}
            </ReactInputMask>
          </FieldShell>
          {errors.contactPhone && (
            <ErrorText>{errors.contactPhone}</ErrorText>
          )}
        </Field>

        <Field>
          <FieldLabel>
            Contact Name <Required>*</Required>
            {isContactsLoading && (
              <InlineLoading>
                <Loader size={12} thickness={5} />
                <span>Loading contacts…</span>
              </InlineLoading>
            )}
          </FieldLabel>
          <FieldShell hasNativeSelect>
            <ContactDropdown inspection={inspection} />
          </FieldShell>
        </Field>
      </TripleGrid>
      )}

      <CriticalCallout role="alert">
        <CriticalIcon>
          <WarningIcon />
        </CriticalIcon>
        <CriticalText>
          <CriticalHeading>Time-frame requests will not be honored.</CriticalHeading>
          <CriticalSub>
            Inspectors are dispatched in route order — please do not
            request a specific time window in the notes.
          </CriticalSub>
        </CriticalText>
      </CriticalCallout>

      <NotesSection>
        <FieldLabel>Notes</FieldLabel>
        <InfoNote>
          <InfoOutlinedIcon style={{ fontSize: 16 }} />
          <span>
            If you wish to provide details for a field contact and their
            phone number, include them below.
          </span>
        </InfoNote>
        <NotesArea
          rows={5}
          value={notes || ''}
          onChange={e => dispatch(setNotes(e.target.value))}
          placeholder="Add any notes for the inspector…"
        />
      </NotesSection>

      {apiMessage && (
        <StatusBanner isApiError={isApiError}>
          {apiMessage}
        </StatusBanner>
      )}
    </Root>
  );
};

export default InspectionsControls;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 120px;
  padding: 24px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingText = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-600);
  letter-spacing: 0.01em;
`;

const InlineLoading = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--neutral-500);
  vertical-align: middle;
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 720px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.div`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Slash = styled.span`
  color: var(--neutral-400);
  font-weight: 600;
  margin: 0 2px;
`;

const HeaderAddress = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--neutral-600);
  font-size: 0.8125rem;
  margin-top: 2px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 0.5fr) minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
`;

const MetaLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetaValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionEyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-top: 4px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;

  @media only screen and (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TripleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div<{ full?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  ${({ full }) => full && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.label<{ error?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ error }) =>
    error ? 'var(--danger-500)' : 'var(--mainText)'};
`;

const Required = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const FieldShell = styled.div<{
  readOnly?: boolean;
  error?: boolean;
  hasNativeSelect?: boolean;
}>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ error }) =>
      error ? 'var(--danger-500)' : 'var(--border)'};
  background: ${({ readOnly }) =>
    readOnly ? 'var(--neutral-100)' : 'var(--neutral-0)'};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ error, readOnly }) =>
      error
        ? 'var(--danger-500)'
        : readOnly
          ? 'var(--border)'
          : 'var(--neutral-400)'};
  }

  &:focus-within {
    border-color: ${({ error }) =>
      error ? 'var(--danger-500)' : 'var(--primary-600)'};
    box-shadow: ${({ error }) =>
      error ? 'none' : 'var(--shadow-focus)'};
  }

  /* Inputs and selects rendered inside should fill */
  > div,
  > input,
  > select,
  .react-datepicker-wrapper,
  .react-datepicker__input-container,
  .react-datepicker__input-container input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: var(--mainText);
    font-family: inherit;
  }

  select {
    cursor: pointer;
    appearance: ${({ hasNativeSelect }) =>
      hasNativeSelect ? 'auto' : 'none'};
  }

  input::placeholder {
    color: var(--neutral-400);
  }
`;

const ReadOnlyValue = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-700);
  font-weight: 500;
  letter-spacing: 0.02em;
`;

const PhoneInput = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: inherit;
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: var(--danger-500);
  margin-top: 2px;
`;

const NotesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const CriticalCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  box-shadow: 0 1px 2px rgba(220, 38, 38, 0.08);
`;

const CriticalIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fee2e2;
  color: #dc2626;
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }
`;

const CriticalText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CriticalHeading = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #991b1b;
  letter-spacing: 0.01em;
`;

const CriticalSub = styled.div`
  font-size: 0.8125rem;
  color: #7f1d1d;
  line-height: 1.45;
`;

const InfoNote = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--neutral-600);
  line-height: 1.45;

  svg {
    color: var(--neutral-500);
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const StatusBanner = styled.div<{ isApiError: boolean }>`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  background: ${({ isApiError }) =>
    isApiError ? 'var(--danger-50)' : '#dcfce7'};
  color: ${({ isApiError }) =>
    isApiError ? 'var(--danger-700)' : '#166534'};
  border: 1px solid
    ${({ isApiError }) =>
      isApiError ? 'var(--danger-200, #fecaca)' : '#bbf7d0'};
`;
